import { Grid, Typography } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import React from 'react'
import styled from 'styled-components'

import { CartFragment } from '../../../graphql/magento'
import { ProductLabelStatus } from '../../../graphql/search'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { shouldShowCaliforniaProp65Asterisks } from '../../../lib/cart/item'
import { useLabelStatusForCartItemSku } from '../../../lib/cart/state/cart-items/cart-item-remove'
import { CartItemVirtualProduct } from '../../../lib/cart/state/types'
import {
  cartItemAndDesignFeePrice,
  cartItemPrice,
  formatAsCurrency,
} from '../../../utils/priceTools'
import LabelStatusIcon from '../../LabelStatusIcon'
import ItemWarningMessages from '../cart/ItemWarningMessages'
import TestKitPolicyMessage from '../cart/TestKitPolicyMessage'
import CartItemImage from '../shared/CartItemImage'
import LabelInformation, { LabelInfoDetails } from '../shared/LabelInformation'
import OrderTypeSummary from './OrderTypeSummary'

const StyledItemAttribute = styled(Grid)`
  line-height: 1.3;
`

const StyledItemSpacer = styled(Grid)`
  height: 5px;
`

const StyledItemTotalFactor = styled(Grid)`
  line-height: 1.3;
`

const StyledItemTotal = styled(Grid)`
  margin-top: 5px;
`

const StyledLabelInformation = styled(LabelInformation)`
  margin: 5px;
  ${LabelInfoDetails} {
    margin-bottom: 16px;
    @media (min-width: 960px) {
      background-color: ${({ theme }) => theme.colors.grayscale.white}
  }
`

const StyledLabelStatusIcon = styled(LabelStatusIcon)`
  position: absolute;
`

const LabelStatus: React.FC<{ sku: string }> = ({ sku }) => {
  const result = useLabelStatusForCartItemSku(sku)
  return result._tag === 'Left' ? null : (
    <StyledLabelStatusIcon
      isLabelPresent={result.data.labelStatus === ProductLabelStatus.Present}
    />
  )
}

export interface CartItemSummaryProps {
  className?: string
  item: NonNullable<NonNullable<CartFragment['items']>[number]>
  labelDesignFeeItem?: CartItemVirtualProduct
  shippingAddress: CartFragment['shipping_addresses'][number]
}

const CartItemSummary: React.FC<CartItemSummaryProps> = ({
  className,
  item,
  labelDesignFeeItem,
  shippingAddress,
}) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  const state = shippingAddress && shippingAddress.region && shippingAddress.region.code
  const sku = item.product.sku
  return (
    <Grid className={className} container item xs={12}>
      <CartItemImage
        sku={item.product.sku}
        label={item.product.name || undefined}
        icon={
          featureFlags?.productLabelStatus &&
          !!sku && (
            <Subscribe fallback={null}>
              <LabelStatus sku={sku} />
            </Subscribe>
          )
        }
      />

      <Grid container direction="column" item xs>
        <Grid item>
          <Typography variant="h4">
            {shouldShowCaliforniaProp65Asterisks({ item, shippingAddress }) ? '** ' : ''}
            {item.product.name}
          </Typography>
        </Grid>
        {!!item.item_attributes?.size && (
          <StyledItemAttribute item>{`Size: ${item.item_attributes.size}`}</StyledItemAttribute>
        )}
        {!!item.item_attributes?.flavor && (
          <StyledItemAttribute item>{`Flavor: ${item.item_attributes.flavor}`}</StyledItemAttribute>
        )}
        {!!item.item_attributes?.dosage && (
          <StyledItemAttribute item>{`Dosage: ${item.item_attributes.dosage}`}</StyledItemAttribute>
        )}
        <StyledItemSpacer />
        <StyledItemTotalFactor item>{`Quantity: ${item.quantity}`}</StyledItemTotalFactor>
        <StyledItemTotalFactor item>
          {`Unit price: ${formatAsCurrency({ amount: item.prices?.price.value || 0 })}`}
        </StyledItemTotalFactor>
        {featureFlags?.productLabelStatus && !!labelDesignFeeItem && (
          <StyledItemTotalFactor item>
            {`Design Fee: ${formatAsCurrency({
              amount: labelDesignFeeItem.prices?.row_total.value || 0,
            })}`}
          </StyledItemTotalFactor>
        )}
        <StyledItemTotal item>
          <strong>
            {featureFlags?.productLabelStatus
              ? `Total: ${cartItemAndDesignFeePrice(item, labelDesignFeeItem)}`
              : `Total: ${cartItemPrice({ item })}`}
          </strong>
        </StyledItemTotal>
        {featureFlags?.productLabelStatus && !!labelDesignFeeItem && (
          <StyledLabelInformation labelItem={labelDesignFeeItem} />
        )}
        <Grid item>
          <ItemWarningMessages messageCode={item.item_attributes?.prop65_message} state={state} />
        </Grid>
        <Grid item>
          <TestKitPolicyMessage sku={item.product.sku || ''} />
        </Grid>
        <OrderTypeSummary
          autoShipInterval={item.item_attributes?.dfh_autoship_interval}
          orderType={item.item_attributes?.dfh_order_type}
        />
      </Grid>
    </Grid>
  )
}

const StyledCartItemSummary = styled(CartItemSummary)`
  padding: 15px;
  background-color: ${({ theme }) => theme.colors.grayscale.white};
  margin-bottom: 1rem;
  border-radius: 8px;
`

export default StyledCartItemSummary
